// EditableText — inline markdown editor for an AI-generated text field.
// Per LLM_NATIVE_ARCHITECTURE.md §2.3 EditableItem (lightweight落地).
//
// Render mode:
//   - Default: shows text as MarkdownBlock + edit button + (if edited) "已编辑"
//     badge + "恢复 AI 原版" button.
//   - Edit mode: shows textarea + 保存 / 取消 buttons.
//
// Caller wires via `onCommit(newText)` (creates / updates ItemOverride in
// progress) and `onRevert()` (deletes the override).

import { useEffect, useRef, useState } from 'react';
import { Pencil, RotateCcw, Check, X } from 'lucide-react';
import { MarkdownBlock } from './markdown-block.js';
import { reportBehaviorError } from '../lib/report-behavior-error.js';
import { useToast } from './toast.js';
import { useT } from '../i18n/index.js';

export interface EditableTextProps {
  /** Current displayed text (resolved override or AI original). */
  text: string;
  /** True if user has edited this field at some point. */
  isEdited: boolean;
  /** Saves a new override. Caller persists to progress.itemOverrides. */
  onCommit(newText: string): Promise<void> | void;
  /** Removes the override. Caller deletes the override entry. */
  onRevert(): Promise<void> | void;
  /** Optional UI labels — defaults are zh, override per-context. */
  labels?: {
    edit?: string;
    save?: string;
    cancel?: string;
    revert?: string;
    revertConfirm?: string;
    editedBadge?: string;
  };
  /** When true, the textarea opens with a larger initial row count. */
  large?: boolean;
}

/** Session 19AS · G11: autosave debounce window. 600ms = the user has clearly
 *  paused typing without making them wait. Picked over 500ms (commonly
 *  recommended) and 1000ms (feels sluggish for short edits). */
const AUTOSAVE_DEBOUNCE_MS = 600;

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export function EditableText({
  text,
  isEdited,
  onCommit,
  onRevert,
  labels,
  large,
}: EditableTextProps) {
  const t = useT();
  const DEFAULT_LABELS = {
    edit: t('common.edit'),
    save: t('common.save'),
    cancel: t('common.cancel'),
    revert: t('editableText.revert'),
    revertConfirm: t('editableText.revertConfirm'),
    editedBadge: t('editableText.editedBadge'),
  };
  const L = { ...DEFAULT_LABELS, ...labels };
  // 19BM-C: revert is destructive (discards user edits). Use a sticky toast
  // with an action button instead of window.confirm — non-blocking, keyboard
  // accessible, and matches the app-wide "no native dialogs" convention.
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  // Session 19AS · G11: autosave state machine.
  //   idle    — no pending changes
  //   pending — typing detected; debounce timer running
  //   saving  — onCommit in flight
  //   saved   — last save succeeded; auto-fades back to idle after 1.2s
  //   error   — last save threw; sticky until user retries
  const [status, setStatus] = useState<SaveStatus>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always commit the latest draft, not the value captured at debounce time.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  function startEdit(): void {
    setDraft(text);
    setStatus('idle');
    setEditing(true);
  }

  // Centralized commit (used by both autosave + explicit save button).
  async function commitDraft(value: string): Promise<void> {
    if (value === text) {
      setStatus('idle');
      return;
    }
    setStatus('saving');
    try {
      await onCommit(value);
      setStatus('saved');
      if (fadeRef.current) clearTimeout(fadeRef.current);
      fadeRef.current = setTimeout(() => setStatus('idle'), 1200);
    } catch {
      // 19BM Work-area A: report the swallowed failure so the operator gains
      // visibility (previously only setStatus('error') ran — red text the user
      // saw, zero signal to the admin feed / agent waterline). UI behavior is
      // unchanged; we additionally report. viewPath is best-effort (location
      // exists in browser + jsdom; guard for SSR/edge).
      const viewPath = typeof location !== 'undefined' ? location.pathname : '';
      void reportBehaviorError({
        viewPath,
        userAction: 'autosave 提交',
        code: 'editable-text-commit',
      });
      setStatus('error');
    }
  }

  function scheduleAutosave(): void {
    setStatus('pending');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void commitDraft(draftRef.current);
    }, AUTOSAVE_DEBOUNCE_MS);
  }

  // Cancel pending timer if component unmounts mid-debounce — but flush the
  // pending draft FIRST so unmounting (route change / view switch) doesn't
  // silently drop the user's last keystrokes.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        // Best-effort flush: only if we still had pending changes.
        if (draftRef.current !== text) {
          // Fire-and-forget; component is unmounting so no state update.
          // onCommit may return void or Promise<void>; wrap in Promise.resolve
          // so .catch() is always available without runtime branching.
          void Promise.resolve(onCommit(draftRef.current)).catch(() => {
            // Swallow — view is gone; failure becomes user-invisible.
          });
        }
      }
      if (fadeRef.current) clearTimeout(fadeRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save(): Promise<void> {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    await commitDraft(draft);
    if (status !== 'error') setEditing(false);
  }

  function cancel(): void {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setDraft(text);
    setStatus('idle');
    setEditing(false);
  }

  async function revert(): Promise<void> {
    // 19BM-C: sticky toast + action replaces window.confirm. The toast stays
    // visible until the user explicitly confirms (action) or dismisses it.
    toast.warn(L.revertConfirm, {
      sticky: true,
      action: { label: L.revert, onClick: () => { void onRevert(); } },
    });
  }

  if (editing) {
    return (
      <div className="editable-text editable-text-editing">
        <textarea
          className="editable-text-textarea"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            scheduleAutosave();
          }}
          rows={large ? 12 : 6}
          autoFocus
        />
        <div className="editable-text-actions">
          <button type="button" className="btn-primary editable-text-btn" onClick={() => { void save(); }}>
            <Check size={13} aria-hidden="true" /> {L.save}
          </button>
          <button type="button" className="btn-secondary editable-text-btn" onClick={cancel}>
            <X size={13} aria-hidden="true" /> {L.cancel}
          </button>
          <SaveIndicator status={status} />
        </div>
      </div>
    );
  }

  return (
    <div className={`editable-text${isEdited ? ' is-edited' : ''}`}>
      <div className="editable-text-content markdown-body">
        <MarkdownBlock>{text}</MarkdownBlock>
      </div>
      {/* `editable-text-content` rendered above shows the persisted markdown.
          Save status doesn't surface in the not-editing view (idle = clean). */}
      <div className="editable-text-toolbar">
        {isEdited && <span className="editable-text-badge">{L.editedBadge}</span>}
        <button
          type="button"
          className="editable-text-btn editable-text-btn-edit"
          onClick={startEdit}
          aria-label={L.edit}
          title={L.edit}
        >
          <Pencil size={12} aria-hidden="true" /> {L.edit}
        </button>
        {isEdited && (
          <button
            type="button"
            className="editable-text-btn editable-text-btn-revert"
            onClick={() => { void revert(); }}
            title={L.revert}
          >
            <RotateCcw size={12} aria-hidden="true" /> {L.revert}
          </button>
        )}
      </div>
    </div>
  );
}

/** Tiny inline save-status indicator next to save / cancel during edit mode.
 *  Idle → invisible. Pending → "正在等待…". Saving → "保存中…". Saved → "已保存".
 *  Error → "保存失败 — 重试".  All under 60px width to avoid layout shift. */
function SaveIndicator({ status }: { status: SaveStatus }) {
  const t = useT();
  if (status === 'idle') return null;
  const map: Record<Exclude<SaveStatus, 'idle'>, { text: string; cls: string }> = {
    pending: { text: t('editableText.statusPending'), cls: 'is-pending' },
    saving:  { text: t('editableText.statusSaving'), cls: 'is-saving'  },
    saved:   { text: t('editableText.statusSaved'),  cls: 'is-saved'   },
    error:   { text: t('editableText.statusError'), cls: 'is-error'  },
  };
  const { text, cls } = map[status];
  return <span className={`editable-text-savestatus ${cls}`}>{text}</span>;
}
